import { calendarDate } from '../services/financialMath';
import { personLedger } from '../services/financialLedger';
import React, { useState, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../contexts/AppContext';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiArrowLeft,
  FiUser,
  FiDollarSign,
  FiPlus,
  FiMinus,
  FiRefreshCw,
  FiMail,
  FiPhone,
  FiCalendar,
  FiFilter,
  FiDownload,
  FiCheck,
} = FiIcons;

const PersonProfile = () => {
  const { id } = useParams();
  const { state: routeState } = useLocation();
  const { state, actions } = useApp();
  const { people, transactions, ious, settings } = state;
  // The People card can open the existing Add IOU form directly; no new data path.
  const [showAddIOU, setShowAddIOU] = useState(Boolean(routeState?.openAddIOU));
  const [initialIOUType, setInitialIOUType] = useState('you_lent');
  const [iouError, setIOUError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: null,
    type: '',
  });

  const person = people.find(p => p.id === parseInt(id));

  const { currentBalance, ledgerEntries: fullLedger } = useMemo(() =>
    person ? personLedger(person, transactions, ious) : {currentBalance:0, ledgerEntries:[]},
    [person,transactions,ious]);
  const ledgerEntries = useMemo(() => fullLedger.filter(entry =>
    filters.type === 'borrowed' ? entry.balanceEffect > 0 :
    filters.type === 'repaid' ? entry.balanceEffect < 0 : true
  ).slice().reverse(), [fullLedger, filters.type]);

  if (!person) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Person not found</h2>
        <Link
          to="/people"
          className="text-primary-600 hover:text-primary-700"
        >
          Back to People
        </Link>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: settings.currency,
    }).format(Math.abs(amount));
  };

  const getBalanceDisplay = (balance) => {
    if (balance === 0) {
      return { text: 'Settled', color: 'text-gray-700', bgColor: 'bg-gray-100' };
    } else if (balance > 0) {
      return { 
        text: `${person.name} owes you ${formatCurrency(balance)}`, 
        color: 'text-success-700', 
        bgColor: 'bg-success-100' 
      };
    } else {
      return { 
        text: `You owe ${person.name} ${formatCurrency(balance)}`, 
        color: 'text-danger-700', 
        bgColor: 'bg-danger-100' 
      };
    }
  };

  const balanceDisplay = getBalanceDisplay(currentBalance);

  const handleSettle = async () => {
    if (currentBalance === 0) return;
    
    // Create settlement IOU
    const settlementAmount = Math.abs(currentBalance);
    const settlementType = currentBalance > 0 ? 'repayment_received' : 'repayment_made';
    
    try {
      await actions.addIOU({personId:person.id,type:settlementType,amount:settlementAmount,date:calendarDate(new Date()),notes:'Balance settlement'});
      setIOUError('');
    } catch (error) { setIOUError(error.message || 'Could not record settlement'); }
  };

  const animationProps = settings.reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.3 },
      };

  return (
    <motion.div className="min-w-0 space-y-4 pb-10 sm:space-y-6" {...animationProps}>
      {/* Header */}
      <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
        <Link
          to="/people"
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
        </Link>
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full">
            <SafeIcon icon={FiUser} className="w-6 h-6 text-primary-600" />
          </div>
          <div className="min-w-0">
            <h1 className="break-words text-xl font-bold text-gray-900 sm:text-2xl">{person.name}</h1>
            {person.shortName && person.shortName !== person.name && (
              <p className="text-gray-600">({person.shortName})</p>
            )}
          </div>
        </div>
      </div>

      {/* Person Info & Balance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Person Details */}
        <motion.div
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          {...animationProps}
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Info</h2>
          <div className="space-y-3">
            {person.contact && (
              <div className="flex min-w-0 items-start gap-2 text-sm">
                <SafeIcon 
                  icon={person.contact.includes('@') ? FiMail : FiPhone} 
                  className="w-4 h-4 mr-3 text-gray-400" 
                />
                <span className="min-w-0 break-all text-gray-900">{person.contact}</span>
              </div>
            )}
            <div className="flex min-w-0 items-start gap-2 text-sm">
              <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-3 text-gray-400" />
              <span className="text-gray-600">
                Added {format(new Date(person.createdAt), 'MMM d, yyyy')}
              </span>
            </div>
            {person.notes && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 italic">"{person.notes}"</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Current Balance */}
        <motion.div
          className={`rounded-lg shadow-sm border border-gray-200 p-6 ${balanceDisplay.bgColor}`}
          {...animationProps}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Running Balance</h2>
            <SafeIcon icon={FiDollarSign} className={`w-6 h-6 ${balanceDisplay.color}`} />
          </div>
          <p className={`mb-4 break-words text-2xl font-bold sm:text-3xl ${balanceDisplay.color}`}>
            {balanceDisplay.text}
          </p>
          {currentBalance !== 0 && (
            <button
              onClick={handleSettle}
              className="w-full flex items-center justify-center px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition-colors duration-200"
            >
              <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
              Settle Balance
            </button>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
          {...animationProps}
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={() => { setInitialIOUType('you_lent'); setShowAddIOU(true); }}
              className="w-full flex items-center justify-center px-4 py-2 bg-success-100 hover:bg-success-200 text-success-700 rounded-lg font-medium transition-colors duration-200"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              You Lent Money
            </button>
            <button
              onClick={() => { setInitialIOUType('you_borrowed'); setShowAddIOU(true); }}
              className="w-full flex items-center justify-center px-4 py-2 bg-danger-100 hover:bg-danger-200 text-danger-700 rounded-lg font-medium transition-colors duration-200"
            >
              <SafeIcon icon={FiMinus} className="w-4 h-4 mr-2" />
              You Borrowed Money
            </button>
            <button
              onClick={() => { setInitialIOUType('repayment_received'); setShowAddIOU(true); }}
              className="w-full flex items-center justify-center px-4 py-2 bg-primary-100 hover:bg-primary-200 text-primary-700 rounded-lg font-medium transition-colors duration-200"
            >
              <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
              Record Repayment
            </button>
          </div>
        </motion.div>
      </div>

      {/* Ledger */}
      <motion.div
        className="bg-white rounded-lg shadow-sm border border-gray-200"
        {...animationProps}
      >
        {/* Ledger Header */}
        <div className="shrink-0 border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Transaction Ledger</h2>
            <div className="mt-4 flex flex-wrap items-center gap-2 sm:mt-0">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  showFilters
                    ? 'bg-primary-100 text-primary-700 border border-primary-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <SafeIcon icon={FiFilter} className="w-4 h-4 mr-2" />
                Filters
              </button>
              <button className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200">
                <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <motion.div
            className="px-6 py-4 border-b border-gray-200 bg-gray-50"
            {...animationProps}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transaction Type
                </label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All transactions</option>
                  <option value="borrowed">Money borrowed from you</option>
                  <option value="repaid">Money repaid to you</option>
                </select>
              </div>
            </div>
          </motion.div>
        )}

        {/* Ledger Entries */}
        <div className="overflow-x-auto">
          {/* Desktop Table */}
          <div className="hidden xl:block">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ledgerEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(entry.date), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        entry.balanceEffect > 0
                          ? 'bg-danger-100 text-danger-800'
                          : 'bg-success-100 text-success-800'
                      }`}>
                        {entry.balanceEffect > 0 ? 'Borrowed' : 'Repaid'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${
                        entry.balanceEffect > 0 ? 'text-danger-600' : 'text-success-600'
                      }`}>
                        {entry.balanceEffect > 0 ? '+' : ''}{formatCurrency(entry.balanceEffect)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {entry.notes || entry.category || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(entry.runningBalance)}
                      {entry.runningBalance > 0 && (
                        <span className="text-xs text-danger-600 ml-1">(owes you)</span>
                      )}
                      {entry.runningBalance < 0 && (
                        <span className="text-xs text-success-600 ml-1">(you owe)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card List */}
          <div className="xl:hidden space-y-4 p-4">
            {ledgerEntries.map((entry) => (
              <div
                key={entry.id}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-900">
                    {format(new Date(entry.date), 'MMM d, yyyy')}
                  </span>
                  <span className={`text-lg font-bold ${
                    entry.balanceEffect > 0 ? 'text-danger-600' : 'text-success-600'
                  }`}>
                    {entry.balanceEffect > 0 ? '+' : ''}{formatCurrency(entry.balanceEffect)}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Type:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      entry.balanceEffect > 0
                        ? 'bg-danger-100 text-danger-800'
                        : 'bg-success-100 text-success-800'
                    }`}>
                      {entry.balanceEffect > 0 ? 'Borrowed' : 'Repaid'}
                    </span>
                  </div>
                  
                  {(entry.notes || entry.category) && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Notes:</span>
                      <span className="text-sm font-medium truncate ml-2">
                        {entry.notes || entry.category}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                    <span className="text-sm text-gray-600">Running Balance:</span>
                    <div className="text-right">
                      <span className="text-sm font-bold text-gray-900">
                        {formatCurrency(entry.runningBalance)}
                      </span>
                      {entry.runningBalance !== 0 && (
                        <p className={`text-xs ${
                          entry.runningBalance > 0 ? 'text-danger-600' : 'text-success-600'
                        }`}>
                          {entry.runningBalance > 0 ? 'owes you' : 'you owe'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Empty State */}
        {ledgerEntries.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiDollarSign} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions yet</h3>
            <p className="text-gray-600 mb-4">
              Start tracking IOUs with {person.name} by adding your first transaction.
            </p>
            <button
              onClick={() => { setInitialIOUType('you_lent'); setShowAddIOU(true); }}
              className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors duration-200"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              Add IOU
            </button>
          </div>
        )}
      </motion.div>

      {iouError && <p role="alert" className="text-red-700">{iouError}</p>}
      {/* Add IOU Modal */}
      {showAddIOU && (
        <AddIOUForm
          person={person}
          initialType={initialIOUType}
          onClose={() => setShowAddIOU(false)}
          onSave={async (iou) => {
            try {
              await actions.addIOU(iou);
              setIOUError('');
              setShowAddIOU(false);
            } catch (error) { setIOUError(error.message || 'Could not save IOU'); throw error; }
          }}
        />
      )}
    </motion.div>
  );
};

// Add IOU Form Component
const AddIOUForm = ({ person, initialType, onClose, onSave }) => {
  const { state } = useApp();
  const { settings } = state;
  const [formData, setFormData] = useState({
    date: calendarDate(new Date()),
    type: initialType,
    createTransaction: false,
    amount: '',
    notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!Number.isFinite(Number(formData.amount)) || Number(formData.amount) <= 0) {
      return;
    }

    const iou = {
      ...formData,
      amount: parseFloat(formData.amount),
      personId: person.id,
      createdAt: new Date(),
    };

    await onSave(iou);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const animationProps = settings.reducedMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.9 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.9 },
        transition: { duration: 0.3 },
      };

  return (
    <motion.div
      className="mobile-full-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      {...animationProps}
    >
      <motion.div
        className="flex w-full max-w-md min-w-0 flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Add IOU with {person.name}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="you_lent">You lent money to {person.name}</option>
              <option value="you_borrowed">You borrowed money from {person.name}</option>
              <option value="repayment_received">Repayment received from {person.name}</option>
              <option value="repayment_made">Repayment made to {person.name}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Optional notes about this IOU..."
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={formData.createTransaction}
              onChange={(e) => handleChange('createTransaction', e.target.checked)} />
            Also record the linked cash transfer (excluded from income/expense totals)
          </label>
        </form>

        {/* Footer */}
        <div className="mobile-modal-actions flex shrink-0 justify-between gap-3 border-t border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!Number.isFinite(Number(formData.amount)) || Number(formData.amount) <= 0}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors duration-200"
          >
            Add IOU
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PersonProfile;